import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Column } from 'primereact/column'
import { useNavigateWithOrigin } from '../../../app/nav/originNav'
import DataTable from '../../../components/ui/DataTable'
import EmptyState from '../../../components/ui/EmptyState'
import SectionCard from '../../../components/ui/SectionCard'
import { CARD_VIEW_BREAKPOINT, useMediaQuery } from '../../../lib/useMediaQuery'
import type { MyActionRow } from '../types'
import { useGrants } from '../../../auth/useCurrentUser'
import { GRANTS } from '../../../auth/grants'

interface Props {
  rows: MyActionRow[]
  loading: boolean
  title: string
  emptyTitle: string
  emptyDesc: string
  footer?: ReactNode
  className?: string
}

/**
 * Raporum'un **"Geciken Aksiyonlar"** tablosu (068 — analiz §2.7; 2026-08-19 revizyonu §5): Aksiyon
 * Tanımı + Gecikme Süresi (gün, kırmızı).
 *
 * <p><b>`metric` prop'u kaldırıldı</b> (2026-08-19): bileşen 068'de iki tabloyu birden besliyordu
 * ("Geciken Aksiyonlar" ve "Yaklaşan Kritik Aksiyon"), ikinci bölüm ise analizin yeni bölüm listesinde
 * yok. Tek çağıran kalınca ayar noktası yalnız dolaylılık üretiyordu.
 *
 * <p>Sabit küçük üst sınırlı (sunucu ≤5 döner) bir **özet bloğudur**, liste ekranı değil → **sayfalama
 * yoktur** (anayasa Page Standards → pano/özet blokları istisnası); tam liste alttaki "Tümünü Gör"
 * bağlantısındadır. Sıra sunucudadır (gecikme ↓ — analiz §5.4); istemci kolon sıralaması sunmaz. Satıra
 * tıklanınca Aksiyon Detay'a gidilir ve anasayfa "origin" olarak taşınır → detaydan "Geri" anasayfaya
 * döner.
 */
export default function MyActionsTable({
  rows,
  loading,
  title,
  emptyTitle,
  emptyDesc,
  footer,
  className,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const navigate = useNavigateWithOrigin()
  // 077 — Kayıt No kolonu yalnız RECORD_NUMBER_VIEW yetkisinde (Özel Kalem). Analizin kolon listesinde
  // yoktur ama yetkiye bağlı bir EK kolondur: 077'de bilinçle eklendi ve yalnız Özel Kalem görür.
  const showRecordNumber = useGrants().hasGrant(GRANTS.RECORD_NUMBER_VIEW)
  // Telefon/tablet genişliğinde tablo yerine kart listesi (ActionsListPanel ile aynı desen: `isMobile` +
  // paylaşılan `ui-card-list` iskeleti) — aynı `rows`/tıklama, yalnız sunum dalı değişir.
  const isMobile = useMediaQuery(CARD_VIEW_BREAKPOINT)

  const goToDetail = (id: number) => navigate(`/minutes/actions/${id}`)

  return (
    <SectionCard
      title={title}
      footer={footer}
      className={`home-crit${className ? ` ${className}` : ''}`}
    >
      {isMobile ? (
        <div className="ui-card-list" style={{ margin: '12px' }}>
          {loading &&
            Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="ui-card ui-card--skeleton" aria-hidden />
            ))}
          {!loading && rows.length === 0 && (
            <EmptyState icon="pi pi-inbox" title={emptyTitle} description={emptyDesc} />
          )}
          {!loading &&
            rows.map((row) => (
              <div
                key={row.id}
                className="ui-card ui-card--clickable"
                role="button"
                tabIndex={0}
                onClick={() => goToDetail(row.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    goToDetail(row.id)
                  }
                }}
              >
                {showRecordNumber && <span className="ui-card-sub">{row.recordNumber}</span>}
                <span className="ui-card-title">{row.name}</span>
                {row.overdueDays != null && (
                  <span className="ui-card-row">
                    <span className="home-crit-late">
                      {t('home.critical.days', { count: row.overdueDays })}
                    </span>
                  </span>
                )}
              </div>
            ))}
        </div>
      ) : (
        <DataTable
          value={rows}
          loading={loading}
          onRowClick={(e) => navigate(`/minutes/actions/${(e.data as MyActionRow).id}`)}
          rowClassName={() => 'home-my-row'}
          emptyMessage={
            <EmptyState icon="pi pi-inbox" title={emptyTitle} description={emptyDesc} />
          }
        >
          {showRecordNumber && (
            <Column field="recordNumber" header={t('home.critical.columns.number')} />
          )}
          {/* Başlık SARAR: bu tablolar `.home-grid`'in YARIM hücresinde duruyor, uzun bir başlık nowrap
              kalsa Yönetici Raporu'nda düzelttiğimiz yatay kaydırma aynı sayfada burada görünürdü. */}
          <Column
            field="name"
            header={t('home.my.pending.columns.name')}
            bodyClassName="tk-cell-strong tk-cell-wrap home-actions-name"
          />
          <Column
            header={t('home.my.columns.overdue')}
            bodyStyle={{ textAlign: 'right' }}
            body={(row: MyActionRow) =>
              // Değeri olmayan hücre BOŞ bırakılır; `-`/`—` yer tutucu yazılmaz.
              row.overdueDays == null ? (
                ''
              ) : (
                <span className="home-crit-late">
                  {t('home.critical.days', { count: row.overdueDays })}
                </span>
              )
            }
          />
        </DataTable>
      )}
    </SectionCard>
  )
}

